import type { Unsubscribe } from '@reduxjs/toolkit';
import type { UserInfo } from '../types/UserInfo.d.ts';
import { sortBy } from './array/sortBy.ts';
import { deepEquals } from './object/deepEquals.ts';
import { objectHas } from './object/objectHas.ts';
import { getHidePhi } from './phi/getHidePhi.ts';
import { isOkResponse, radHttp } from './rad-http/radHttp.ts';
import { capitalize } from './string/capitalize.ts';
import { meSelector, organizationSelector } from '../redux/app/selectors.ts';
import { getStore } from '../redux/store/getStore.ts';
import { addToast } from '../redux/toasts/actions/addToast.ts';

export const DOWNLOADS_POLL_DELAY = 30 * 1000; // ms

type Listener = (items: DownloadItem[]) => void;

export interface DownloadFilter {
  statuses?: DownloadStatus[];
  types?: DownloadType[];
  hasFile?: boolean;
}

export const DownloadStatus = {
  Started: 'Started',
  Completed: 'Completed',
  Failed: 'Failed'
} as const;

export type DownloadStatus = ValueOf<typeof DownloadStatus>;

const finishedStatuses: DownloadStatus[] = [DownloadStatus.Completed, DownloadStatus.Failed] as const;

export const DownloadType = {
  AnonymizedPatient: 'AnonymousPatient',
  NonAnonymizedPatient: 'NonAnonymizedPatient',
  AnonymizedImage: 'AnonymizedImage',
  NonAnonymizedImage: 'NonAnonymizedImage'
} as const;

export type DownloadType = ValueOf<typeof DownloadType>;

export interface DownloadItem {
  patientSer: number;
  patientId: string;
  organizationId: string;
  userId: string;
  firstName: string;
  lastName: string;
  id: string;
  hasFile: boolean;
  creationDate: string;
  userNotified: boolean;
  status: DownloadStatus;
  type: DownloadType;

  courseSer?: number;
  rtPlanSer?: number;
  fraction?: number;
  index?: number;

  saveId: string;
}

/**
 * Returns the displayable download type string.
 * @param type
 * @param capitalized If true, returned with the words capitalized.
 */
export function getDownloadTypeString(type: DownloadType, capitalized: boolean = false): string {
  const transform: (v: string) => string = capitalized ? capitalize : v => v;

  switch (type) {
    case DownloadType.AnonymizedImage:
      return transform('anonymized image');
    case DownloadType.AnonymizedPatient:
      return transform('anonymized patient');
    case DownloadType.NonAnonymizedImage:
      return transform('non-anonymized image');
    case DownloadType.NonAnonymizedPatient:
      return transform('non-anonymized patient');
  }

  return transform('download');
}

export class DownloadManager {
  private pollTimeoutId?: number;
  private listeners: Listener[] = [];
  private items?: DownloadItem[];
  private me?: UserInfo;
  private unsubscribeStore?: Unsubscribe;

  async enable() {
    this.unsubscribeStore = getStore().subscribe(this.stateChanged);

    await this.poll(true, true, true);
  }

  disable() {
    clearTimeout(this.pollTimeoutId);

    this.unsubscribeStore?.();
    this.unsubscribeStore = undefined;
  }

  /**
   * Gets the items.
   * If force, forces a re-poll before returning.
   */
  async get(force: boolean = false): Promise<DownloadItem[]> {
    if (!this.items || force) {
      await this.poll();
    }

    return this.items ?? [];
  }

  /**
   * Returns the current list of items filtered by the options.
   * Defaults to providing only completed items with files.
   * If force, forces a re-poll before returning.
   */
  async filtered(filter: DownloadFilter = {}, force = false): Promise<DownloadItem[]> {
    const statuses = objectHas(filter, 'statuses')
      ? filter.statuses
      : [DownloadStatus.Completed];

    const hasFile = objectHas(filter, 'hasFile')
      ? filter.hasFile
      : true;

    const types = objectHas(filter, 'types')
      ? filter.types
      : [];

    return (await this.get(force)).filter(item =>
      (!statuses?.length || statuses?.includes(item.status))
        && (!types?.length || types?.includes(item.type))
        && (hasFile === undefined || item.hasFile === hasFile)
    );
  }

  addListener(callback: Listener) {
    this.listeners.push(callback);
  }

  removeListener(callback: Listener) {
    const index = this.listeners.indexOf(callback);

    if (index === -1) return;

    this.listeners.splice(index, 1);
  }

  /** Triggers download of the item. */
  async downloadItem(item: DownloadItem, extension = 'zip') {
    if (!item) return;

    const response = await radHttp.get<Blob>(
      `./weekly-check/api/support/download/{organization}/${item.id}`,
      { type: 'blob' }
    );

    if (isOkResponse(response)) {
      this.downloadBlob(response.data, `${item.saveId}.${extension}`);
    } else {
      void addToast(
        `There was a problem downloading the ${getDownloadTypeString(item.type)}.`,
        { color: 'var(--bad-color)' }
      );
    }
  }

  /** Downloads the given data as a text file. */
  downloadText(data: string, filename: string = 'download.txt', mimeType: string = 'text/plain'){
    this.download(`data:${mimeType};charset=utf-8,${encodeURIComponent(data)}`, filename);
  };

  /**
   * Downloads the given blob.
   */
  downloadBlob(data: Blob, filename: string = 'download.dat') {
    this.download(window.URL.createObjectURL(data), filename);
  }

  /**
   * Downloads the given data as JSON.
   * If given a string, it assumes the data is already stringified.
   * Otherwise, it stringifies before creating.
   */
  downloadJson(data: object | string, filename = 'download.json') {
    const downloadStr = typeof data === 'object'
      ? JSON.stringify(data, null, 2)
      : data;

    this.downloadText(downloadStr, filename, 'application/json');
  };

  /** Triggers a download of the given dataUrl. */
  private download(dataUrl: string, filename: string) {
    const element = document.createElement('a');
    element.setAttribute('href', dataUrl);
    element.setAttribute('download', filename);

    element.style.display = 'none';
    document.body.appendChild(element);

    element.click();

    document.body.removeChild(element);
  }

  /**
   * Polls for new downloads.
   * @param loop If true, this will kick off the polling loop.
   * @param notify If true, newly completed items will notify users.
   * @param downloadCompleted If true, any newly completed items will download.
   */
  private async poll(loop = false, notify = false, downloadCompleted = false) {
    // If we don't have an organization, the poll will fail, so just skip it entirely.
    if (organizationSelector()) {
      try {
        const response = await radHttp.get<DownloadItem[]>('./weekly-check/api/support/downloads/{organization}');

        if (!isOkResponse(response)) return;

        await this.processItems(response.data, notify, downloadCompleted);

        if (!deepEquals(this.items, response.data)) {
          this.items = response.data.sort(sortBy(['creationDate', -1]));

          for (const listener of this.listeners) {
            listener(this.items);
          }
        }
      } catch (ex) {
        console.error('Error polling downloads', ex);
      }
    }

    if (loop) {
      this.pollTimeoutId =
        setTimeout(() => { void this.poll(true, notify, downloadCompleted); }, DOWNLOADS_POLL_DELAY);
    }
  }

  /**
   * Processes items.
   * @param notify If true, newly completed items will notify users.
   * @param downloadCompleted If true, any newly completed items will download.
   * @param items
   * @param downloadCompleted
   */
  private async processItems(items: DownloadItem[], notify: boolean = false, downloadCompleted: boolean = false) {
    if (!this.me) return;

    // Get all the download items that are finished processing that the user hasn't been notified about
    const newlyProcessedItems = items.filter(item =>
      item.userId === this.me?.chartCheckId
      && finishedStatuses.includes(item.status)
      && !item.userNotified
    ).sort(sortBy(['creationDate', -1]));

    if (notify || downloadCompleted) {
      for (const item of newlyProcessedItems) {
        if (item.status === DownloadStatus.Completed) {
          if (notify) {
            void addToast(
              getHidePhi()
                ? `Download of ${getDownloadTypeString(item.type)} ready for patient ${item.patientSer}. `
                  + 'Click show available downloads in the info menu to download.'
                : `Download of ${getDownloadTypeString(item.type)} ready for ${item.lastName}, ${item.firstName} (${item.patientId}). `
                  + 'Click show available downloads in the info menu to download.'
            );
          }

          if (downloadCompleted) {
            await this.downloadItem(item);
          }
        } else if (item.status === DownloadStatus.Failed) {
          if (notify) {
            void addToast(
              getHidePhi()
                ? `Failed to create download for patient ${item.patientSer}.`
                : `Failed to create download for ${item.lastName}, ${item.firstName} (${item.patientId}).`,
              { color: 'var(--bad-color)' }
            );
          }
        }

        if (notify) {
          await radHttp.put(
            `./weekly-check/api/support/download/{organization}/{itemId}/user-notified`,
            {
              type: 'blob',
              params: { itemId: item.id }
            }
          );
        }
      }
    }
  }

  // Must be an arrow function.
  private stateChanged = () => {
    this.me = meSelector(getStore().getState());
  };
}

export const downloads = new DownloadManager();